import { segmentShares, toneSegments, toPercent } from './scale';
import { formatNumber } from '@/shared/lib/format';
import { TONE_COLORS } from '@/shared/lib/tones';
import { cn } from '@/shared/lib/utils';
import type { StatusCounts } from '@/shared/types/domain';

export interface ColumnDatum {
  key: string;
  /** O rótulo do eixo — curto, porque em 375px cabem três letras. */
  label: string;
  /** A leitura por extenso, para o `title` e o leitor de tela. */
  description: string;
  counts: StatusCounts;
  /** O período corrente, marcado para dar ponto de referência ao olho. */
  current?: boolean;
}

interface ColumnChartProps {
  data: readonly ColumnDatum[];
  className?: string;
}

/**
 * Volume por período, empilhado por situação.
 *
 * **É o bloco que responde "estou acelerando ou parando?"** — a pergunta que
 * nenhuma tela do painel respondia, porque todas mostram um recorte do presente.
 * A altura diz quanto; o empilhamento diz em que pé; a coluna marcada diz onde
 * é agora.
 *
 * **`div` com altura em porcentagem, e não `<svg>`.** Colunas empilhadas são
 * retângulos alinhados, que é exatamente o que o flexbox faz — e assim a cor de
 * cada faixa é a MESMA classe do crachá e do chip do calendário
 * ([tones.ts](../../lib/tones.ts)). Em SVG ela entraria por `fill-*`, e a paleta
 * teria de existir em duas formas para servir aos dois desenhos.
 *
 * ⚠️ **Coluna vazia não recebe piso** — ver `toPercent`. Um mês sem projeto
 * mostra o trilho e nada mais, porque o gráfico não deve afirmar atividade onde
 * não houve.
 *
 * ⚠️ **Cor nunca é o único portador** (US-14): cada coluna carrega o total
 * escrito acima dela, e o `title` diz o período e a composição por extenso.
 */
export function ColumnChart({ data, className }: ColumnChartProps) {
  // Uma passada só, e nada de arrays paralelos indexados por posição: com
  // `noUncheckedIndexedAccess`, `totals[index]` chega como `number | undefined`
  // e a alternativa seria um `?? 0` que engoliria um desalinhamento real entre
  // as listas.
  const columns = data.map((datum) => {
    const segments = toneSegments(datum.counts);
    return {
      datum,
      segments,
      shares: segmentShares(segments),
      total: segments.reduce((sum, segment) => sum + segment.value, 0),
    };
  });
  const max = Math.max(...columns.map((column) => column.total), 0);

  return (
    <div className={cn('flex items-end gap-1 sm:gap-1.5', className)}>
      {columns.map(({ datum, segments, shares, total }) => {
        return (
          <div
            key={datum.key}
            className="flex min-w-0 flex-1 flex-col items-center gap-1.5"
            title={datum.description}
          >
            {/*
              O total fica ACIMA da coluna, e não dentro: dentro, ele sumiria
              nas colunas baixas — justamente as que a pessoa precisa conferir
              para saber se são 1 ou 2.
            */}
            <span
              className={cn(
                'tabular text-xs',
                total === 0 ? 'text-muted-foreground/50' : 'font-medium text-foreground',
              )}
            >
              {total === 0 ? '–' : formatNumber(total)}
            </span>

            {/*
              Duas caixas, e a divisão de trabalho entre elas é o que mantém o
              gráfico honesto: a de FORA é o trilho de altura fixa; a de DENTRO
              tem a altura do total contra o máximo da série, e é só dentro dela
              que os segmentos se dividem. Com o piso aplicado a cada segmento,
              três faixas de 1 ficavam mais altas que uma faixa de 3 — a altura
              deixava de significar volume, que é a única coisa que ela promete.
            */}
            <div
              aria-hidden="true"
              className={cn(
                'flex h-24 w-full flex-col justify-end overflow-hidden rounded-md sm:h-28',
                datum.current ? 'bg-accent' : 'bg-muted/60',
              )}
            >
              <div
                className="flex w-full flex-col-reverse"
                style={{ height: `${toPercent(total, max)}%` }}
              >
                {segments.map((segment, segmentIndex) => (
                  <div
                    key={segment.key}
                    className={cn('w-full shrink-0', TONE_COLORS[segment.tone].solid)}
                    style={{ height: `${shares[segmentIndex]}%` }}
                  />
                ))}
              </div>
            </div>

            <span
              className={cn(
                'w-full truncate text-center text-xs',
                datum.current ? 'font-semibold text-foreground' : 'text-muted-foreground',
              )}
            >
              {datum.label}
            </span>
          </div>
        );
      })}
    </div>
  );
}
